import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import {
  formatTaskRef,
  taskStatusLabelFr,
  TASK_TYPE_LABELS_FR,
  type ProjectSnapshot,
  type Task,
  type TaskDetail,
  type TaskListUser,
  type TaskRender,
  type User,
} from "@relais/shared";
import { STATUS_ACCENT } from "@relais/design-system";
import { X } from "lucide-react";
import * as Sentry from "@sentry/react";
import { apiFetch } from "../../lib/api.js";
import { useOffline } from "../../lib/offline-context.js";
import { DocumentsCarousel } from "../../features/task-detail/DocumentsCarousel.js";
import { LockedClientNotice } from "../../features/task-detail/LockedClientNotice.js";
import { TaskInfoCard } from "../../features/task-detail/TaskInfoCard.js";
import { SubmittedNotice } from "../../features/task-detail/SubmittedNotice.js";
import { QuestionedNotice } from "../../features/task-detail/QuestionedNotice.js";
import { CompletedNotice } from "../../features/task-detail/CompletedNotice.js";
import { RefusedNotice } from "../../features/task-detail/RefusedNotice.js";
import { ActionBar } from "../../features/task-detail/ActionBar.js";
import { TaskSyncBanner } from "../../features/task-detail/TaskSyncBanner.js";
import { TaskPdfButton } from "../../features/task-detail/pdf/TaskPdfButton.js";

const LOCAL_SAVE_ERROR_FR =
  "Enregistrement local impossible — réessayez dans un instant.";

export const Route = createFileRoute("/tasks/$taskId")({
  component: TaskDetail,
});

function TaskDetail() {
  const { taskId } = Route.useParams();
  const { service, refresh, flush, online, pushToast, tick, lastSync } =
    useOffline();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const [task, setTask] = useState<Task | null>(null);
  // Référent : amorcé depuis le cache hors-ligne (TaskListUser), puis enrichi
  // par le TaskDetail complet (User) quand le réseau est dispo.
  const [supervisor, setSupervisor] = useState<User | TaskListUser | null>(
    null,
  );
  const [render, setRender] = useState<TaskRender | null>(null);
  const [busy, setBusy] = useState(false);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!service) return;
    service.getTask(taskId).then(setTask);
    // Référent en cache : consultable même hors-ligne.
    service.getCachedSupervisor(taskId).then((s) => {
      if (s) setSupervisor(s);
    });
  }, [service, taskId]);

  // Dé-assignation pendant la consultation : la boucle de sync purge la tâche
  // du cache et bumpe `tick`. Dès que le cache ne la connaît plus, retour au
  // dashboard (le toast vient de l'observer de sync). Couvre aussi le deep
  // link vers une tâche inconnue du cache, qui restait sinon sur "Chargement…".
  useEffect(() => {
    if (!service) return;
    let cancelled = false;
    service.getTask(taskId).then((t) => {
      if (cancelled) return;
      // Dé-assignation : la tâche a disparu du cache → retour dashboard.
      // Sinon on réaffiche l'état du cache, rafraîchi par la boucle de sync
      // (tick) : une page laissée ouverte reflète les propositions/date
      // arrivées entre-temps sans avoir à la rouvrir.
      //
      // `lastSync` garde le renvoi : tant qu'aucun cycle de sync n'a abouti
      // dans cette session, un cache muet ne veut PAS dire « pas à moi » — il
      // veut dire « pas encore rempli ». Sans ce garde, un lien de mission
      // ouvert juste après une reconnexion (cache froid) renvoyait au
      // dashboard au lieu d'ouvrir la mission demandée. On reste sur
      // « Chargement… » le temps du premier cycle.
      if (t === null) {
        if (lastSync !== null) navigate({ to: "/" });
      } else setTask(t);
    });
    return () => {
      cancelled = true;
    };
  }, [service, taskId, tick, lastSync, navigate]);

  // Online : rafraîchit la tâche affichée depuis le serveur. Snapshot CRM et
  // tâche autoritative sont fusionnés en UNE écriture du cache (source de
  // vérité offline) pour éviter que les deux refresh se court-circuitent. Le
  // cache et l'affichage ne changent que si le fetch de la tâche réussit.
  useEffect(() => {
    if (!service || !task || !online) return;
    const svc = service;
    const id = task.id;
    const projectId = task.project_id;
    let cancelled = false;

    (async () => {
      const [snapRes, detailRes] = await Promise.allSettled([
        apiFetch<ProjectSnapshot | null>(
          `/projects/${encodeURIComponent(projectId)}`,
        ),
        apiFetch<TaskDetail>(`/tasks/${id}`),
      ]);
      if (cancelled) return;

      const snapshot =
        snapRes.status === "fulfilled" ? snapRes.value : null;
      if (snapRes.status === "rejected")
        console.warn("[task] snapshot refresh failed", snapRes.reason);
      const detail =
        detailRes.status === "fulfilled" ? detailRes.value : null;
      if (detailRes.status === "rejected")
        console.warn("[task] detail fetch failed", detailRes.reason);

      if (detail) {
        setSupervisor(detail.supervisor);
        // Snapshot CRM frais prioritaire sur celui (potentiellement plus vieux)
        // renvoyé par le serveur ; upsertTasks rejoue les mutations en vol.
        const merged: Task = snapshot
          ? { ...detail, project_snapshot: snapshot }
          : detail;
        await svc.upsertTasks([merged]);
      } else if (snapshot) {
        // Détail indisponible : on rafraîchit au moins le snapshot CRM.
        await svc.updateTaskSnapshot(id, snapshot);
      } else {
        return; // rien n'a abouti : on ne touche pas à l'affichage
      }
      if (cancelled) return;
      const fresh = await svc.getTask(id);
      if (!cancelled && fresh) setTask(fresh);
    })().catch((err) => console.warn("[task] refresh failed", err));

    apiFetch<{ render: TaskRender | null }>(`/tasks/${id}/render`)
      .then((d) => {
        if (cancelled) return;
        setRender(d.render);
      })
      .catch((err) => console.warn("[task] render fetch failed", err));

    return () => {
      cancelled = true;
    };
  }, [service, task?.id, task?.project_id, task?.accepted_at, online]);

  if (matchRoute({ to: "/tasks/$taskId/render", params: { taskId }, fuzzy: true })) {
    return <Outlet />;
  }
  if (!task) return <p className="text-muted-foreground">Chargement…</p>;

  // Les coordonnées client et les documents ne sont révélés qu'une fois la
  // mission acceptée.
  const revealed = task.accepted_at != null;

  async function action(kind: "accept" | "start" | "submit" | "resume") {
    if (!service) return;
    if (kind === "accept" && !online) {
      pushToast({ kind: "error", message: "Connexion requise pour accepter la mission." });
      return;
    }
    setBusy(true);
    try {
      try {
        await service.enqueue({ kind, task_id: taskId });
      } catch (err) {
        // Dexie peut timeouter (iOS Safari) : on prévient au lieu de perdre
        // l'action en silence, et on ne bloque jamais le parcours — pour un
        // start, le formulaire reste accessible et le submit ré-enqueuera la
        // transition manquante (auto-heal offline). Message fixe : le
        // err.message Dexie (anglais, technique) part à Sentry, pas au relais.
        Sentry.captureException(err);
        pushToast({ kind: "error", message: LOCAL_SAVE_ERROR_FR });
        if (kind === "start") {
          navigate({ to: "/tasks/$taskId/render", params: { taskId } });
        }
        return;
      }
      if (kind === "accept") {
        // Online-only : on pousse l'acceptation tout de suite ; le serveur
        // renvoie la tâche complète (données client révélées) qui repeuple le
        // cache. Si le flush échoue, l'acceptation reste en file et sera
        // rejouée par la boucle de sync ; on prévient l'utilisateur.
        try {
          await flush();
        } catch {
          pushToast({
            kind: "error",
            message:
              "Acceptation enregistrée, mais synchronisation échouée. Les données client s'afficheront à la prochaine connexion.",
          });
        }
        const t = await service.getTask(taskId);
        setTask(t);
      } else {
        const t = await service.getTask(taskId);
        setTask(t);
        if (kind === "start" && t) {
          navigate({ to: "/tasks/$taskId/render", params: { taskId } });
        }
        refresh().catch(() => {});
      }
    } finally {
      setBusy(false);
    }
  }

  async function refuse() {
    if (!service || !reason.trim()) return;
    setBusy(true);
    try {
      try {
        await service.enqueue({
          kind: "refuse",
          task_id: taskId,
          reason: reason.trim(),
        });
      } catch (err) {
        // Échec local (Dexie) : toast + drawer laissé ouvert pour re-tenter.
        Sentry.captureException(err);
        pushToast({ kind: "error", message: LOCAL_SAVE_ERROR_FR });
        return;
      }
      setRefuseOpen(false);
      setReason("");
      refresh().catch(() => {});
      navigate({ to: "/" });
    } finally {
      setBusy(false);
    }
  }

  // Sélection d'un créneau : persiste dans le cache avant la resync pour que le
  // reload (même offline) reste cohérent si le refresh échoue.
  async function onSlotSelected(updated: Task) {
    await service?.upsertTasks([updated]).catch(() => {});
    await refresh().catch(() => {});
    const t = service ? await service.getTask(taskId) : null;
    setTask(t ?? updated);
  }
  function onSlotConflict() {
    refresh()
      .catch(() => {})
      .then(() => service?.getTask(taskId))
      .then((t) => {
        if (t) setTask(t);
      });
  }

  // En-tête : pastille de statut, titre, fermeture.
  const header = (
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <span
          className={
            "inline-block rounded-[15px] px-3 py-1 text-xs font-normal " +
            STATUS_ACCENT[task.status].badge
          }
        >
          {taskStatusLabelFr(task)}
        </span>
        <h2 className="mt-3 text-[22px] font-medium leading-tight text-[#06070C]">
          {TASK_TYPE_LABELS_FR[task.type]}
        </h2>
        {/* Référence en clair : c'est la clé que portent les emails et les SMS de
            relance (« votre visite #44381F58 »). Sans elle à l'écran, le relais
            ne peut pas relier le message reçu à sa mission. */}
        <p
          data-testid="task-ref"
          className="mt-1 font-mono text-xs tracking-wide text-[#616269]"
        >
          {formatTaskRef(task.id)}
        </p>
      </div>
      <button
        onClick={() => navigate({ to: "/" })}
        aria-label="Fermer"
        className="grid size-10 shrink-0 place-items-center rounded-[10px] border border-[#919191] text-[#06070C] active:scale-95"
      >
        <X className="size-5" aria-hidden />
      </button>
    </header>
  );

  // Panneau de bas de page propre à l'état (réassurance / commentaire référent
  // / clôture). Les états sans panneau renvoient null.
  function statusNotice(): ReactNode {
    switch (task!.status) {
      case "submitted":
        return <SubmittedNotice supervisor={supervisor} />;
      case "questioned":
        return (
          <QuestionedNotice
            supervisor={supervisor}
            notes={render?.review_notes ?? null}
          />
        );
      case "completed":
        return <CompletedNotice />;
      case "refused":
        return (
          <RefusedNotice
            supervisor={supervisor}
            notes={render?.review_notes ?? task!.refusal_reason ?? null}
          />
        );
      default:
        return null;
    }
  }

  // Coque unique commune à tous les états. Les éléments contextuels s'auto-
  // masquent : TaskPdfButton (in_progress/submitted), ActionBar (selon statut),
  // refuseModal (selon ouverture), et le corps bascule documents ↔ verrou
  // selon que la mission est acceptée.
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 pb-28">
      {header}

      <TaskPdfButton
        task={task}
        conducteurName={
          supervisor
            ? `${supervisor.first_name ?? ""} ${supervisor.last_name ?? ""}`.trim() ||
              undefined
            : undefined
        }
      />
      <TaskSyncBanner taskId={task.id} />

      <TaskInfoCard
        task={task}
        supervisor={supervisor}
        online={online}
        onSlotSelected={onSlotSelected}
        onSlotConflict={onSlotConflict}
      />

      {revealed ? (
        <DocumentsCarousel
          snapshot={task.project_snapshot}
          taskId={taskId}
          attachedDocumentIds={task.spec?.attachedDocumentIds}
          uploads={task.spec?.uploads}
        />
      ) : (
        <LockedClientNotice />
      )}

      {statusNotice()}

      <ActionBar
        task={task}
        busy={busy}
        online={online}
        onAccept={() => action("accept")}
        onRefuse={() => setRefuseOpen(true)}
        onStart={() => action("start")}
        onSubmit={() => action("submit")}
        onOpenRender={() =>
          navigate({ to: "/tasks/$taskId/render", params: { taskId } })
        }
        onOpenReport={() =>
          navigate({ to: "/tasks/$taskId/rapport", params: { taskId } })
        }
        onResume={() => action("resume")}
      />

      {refuseOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm space-y-3 rounded-lg bg-background p-4">
            <h3 className="font-semibold">Motif du refus</h3>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-24 w-full rounded-md border bg-background px-3 py-2"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setRefuseOpen(false);
                  setReason("");
                }}
                className="rounded-md border px-3 py-1.5 text-sm"
              >
                Annuler
              </button>
              <button
                onClick={refuse}
                disabled={!reason.trim() || busy}
                className="rounded-md bg-destructive px-3 py-1.5 text-sm text-destructive-foreground"
              >
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
